import { cn } from "@/common/helpers/utils";
import { SectionTabTriggers } from "@/components/detail-page/_components/tabbar/section-tab-triggers";
import { useScrollableTabBarScroll } from "@/components/detail-page/_hooks/use-scrollable-tab-bar-scroll";
import type { DetailTab } from "@/components/detail-page/types";
import { TabsList } from "@/components/ui/tabs";
import { useEffect, useRef, type ReactElement } from "react";

interface ScrollableTabBarProps {
  sections: DetailTab[];
  activeTab: string | undefined;
}

export function ScrollableTabBar({ sections, activeTab }: ScrollableTabBarProps): ReactElement {
  const stripRef = useRef<HTMLDivElement>(null);
  const { canScrollLeft, canScrollRight } = useScrollableTabBarScroll(stripRef);

  useEffect(() => {
    const activeTrigger = stripRef.current?.querySelector<HTMLElement>(
      '[data-slot="tabs-trigger"][data-state="active"]',
    );
    activeTrigger?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [activeTab]);

  return (
    <div className="relative">
      <div ref={stripRef} className="border-border scrollbar-none overflow-x-auto overscroll-x-contain border-b">
        <TabsList className="w-max border-b-0">
          <SectionTabTriggers sections={sections} />
        </TabsList>
      </div>
      {canScrollLeft && <ScrollableTabBarEdgeFade side="left" />}
      {canScrollRight && <ScrollableTabBarEdgeFade side="right" />}
    </div>
  );
}

function ScrollableTabBarEdgeFade({ side }: { side: "left" | "right" }): ReactElement {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "from-background pointer-events-none absolute inset-y-0 w-10 to-transparent",
        side === "left" ? "left-0 bg-linear-to-r" : "right-0 bg-linear-to-l",
      )}
    />
  );
}
